import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';

import { getLocalTime } from '../../../../common/utils/time/time';
import { TextInput } from '../../../components';
import Text from '../../../components/text/Text';
import { Card, PrimaryButton, SecondaryButton, Sheet, WarningBar } from '../../../design/components';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

export const MANAGER_REMINDER = 'You must inform your manager that you cannot service this POS.';

const OTHER = 'Other';
const REASONS = ['No access', 'Ran out of time', 'Machine fault', OTHER];

/**
 * CouldNotCompleteSheet: the driver gives a reason for a stop they cannot
 * complete. A quick choice is required; the details box is required for Other.
 * Props: isVisible, onClose, title, onSubmit(reason) (async), isSubmitting
 */
export const CouldNotCompleteSheet = ({ isVisible, onClose, title, onSubmit, isSubmitting }) => {
	const styles = useThemedStyles(createStyles);
	const [choice, setChoice] = useState(null);
	const [details, setDetails] = useState('');

	// Every opening starts empty.
	useEffect(() => {
		if (isVisible) {
			setChoice(null);
			setDetails('');
		}
	}, [isVisible]);

	const trimmedDetails = details.trim();
	const reason = choice === OTHER
		? trimmedDetails
		: [choice, trimmedDetails].filter(Boolean).join(' - ');
	const canSubmit = Boolean(choice) && Boolean(reason);

	return (
		<Sheet
			isVisible={isVisible}
			onClose={onClose}
			title={title}
			footer={(
				<View style={styles.footer}>
					<PrimaryButton
						label="Mark as couldn't complete"
						onPress={() => onSubmit(reason)}
						loading={isSubmitting}
						disabled={!canSubmit}
					/>
					<SecondaryButton label="Cancel" onPress={onClose} />
				</View>
			)}
		>
			<WarningBar items={[MANAGER_REMINDER]} />
			<Text style={styles.label}>Reason</Text>
			<View style={styles.options} accessibilityRole="radiogroup">
				{REASONS.map((option) => {
					const isSelected = option === choice;
					return (
						<Pressable
							key={option}
							onPress={() => setChoice(option)}
							accessibilityRole="radio"
							accessibilityLabel={option}
							accessibilityState={{ selected: isSelected, checked: isSelected }}
							style={({ pressed }) => [styles.option, isSelected && styles.optionSelected, pressed && styles.pressed]}
						>
							<Text style={styles.optionTitle}>{option}</Text>
							<View style={[styles.radio, isSelected && styles.radioSelected]}>
								{isSelected && <View style={styles.radioDot} />}
							</View>
						</Pressable>
					);
				})}
			</View>
			<TextInput
				value={details}
				onChange={setDetails}
				placeholder={choice === OTHER ? 'What happened? (required)' : 'Add details (optional)'}
				multiline={true}
				maxLength={250}
				style={styles.details}
				accessibilityLabel="Details"
			/>
		</Sheet>
	);
};

/**
 * CouldNotCompleteCard: a stop marked "Couldn't complete", with its reason,
 * the time, the manager reminder and Undo.
 * Props: couldNotComplete ({ reason, at }), timezone, onUndo (no Undo button without it), isUndoing
 */
export const CouldNotCompleteCard = ({ couldNotComplete, timezone, onUndo, isUndoing }) => {
	const styles = useThemedStyles(createStyles);

	return (
		<Card tone="warning" style={styles.card}>
			<Text style={styles.cardTitle}>Couldn&apos;t complete</Text>
			<Text style={styles.cardReason}>{couldNotComplete.reason}</Text>
			<Text style={styles.cardMeta}>{`Marked at ${getLocalTime(timezone, couldNotComplete.at).format('h:mm a')}`}</Text>
			<Text style={styles.cardReminder}>{MANAGER_REMINDER}</Text>
			{Boolean(onUndo) && <SecondaryButton label="Undo" onPress={onUndo} loading={isUndoing} style={styles.undo} />}
		</Card>
	);
};

const createStyles = ({ ui: { colors, radius, space, type } }) => ({
	footer: {
		gap: space.sm,
	},
	label: {
		...type.fieldLabel,
		color: colors.text,
		marginTop: space.xs,
	},
	options: {
		gap: space.sm,
	},
	option: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
		minHeight: 52,
		paddingHorizontal: 14,
		paddingVertical: 10,
		borderRadius: radius.hero,
		borderWidth: 1,
		borderColor: colors.border,
		backgroundColor: colors.card,
	},
	optionSelected: {
		borderWidth: 2,
		borderColor: colors.currentBorder,
		backgroundColor: colors.current,
	},
	pressed: {
		opacity: 0.85,
	},
	optionTitle: {
		...type.bodyStrong,
		color: colors.text,
		flex: 1,
	},
	radio: {
		width: 26,
		height: 26,
		borderRadius: radius.pill,
		borderWidth: 2,
		borderColor: colors.iconMuted,
		alignItems: 'center',
		justifyContent: 'center',
	},
	radioSelected: {
		borderColor: colors.currentBorder,
	},
	radioDot: {
		width: 12,
		height: 12,
		borderRadius: radius.pill,
		backgroundColor: colors.currentBorder,
	},
	details: {
		minHeight: 72,
		textAlignVertical: 'top',
	},
	card: {
		gap: space.xs,
	},
	cardTitle: {
		...type.sectionTitle,
		color: colors.warning,
	},
	cardReason: {
		...type.body,
		color: colors.text,
	},
	cardMeta: {
		...type.secondary,
		color: colors.textSecondary,
	},
	cardReminder: {
		...type.bodySmallSemibold,
		color: colors.text,
		marginTop: space.xs,
	},
	undo: {
		marginTop: space.sm,
	},
});
